import { useEffect, useState } from "react";
import api from "@/shared/lib/api";
import { PageHeader, Stat } from "@/shared/components/UI";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Briefcase, Clock, Palette, CheckCircle2,
  Hammer, Truck, AlertTriangle,
} from "lucide-react";
import ActionQueueWidget from "@/shared/components/ActionQueueWidget";
import { useAuth } from "@/shared/lib/auth";
import { ScopeChangeRequests } from "@/features/boq/components/ScopeChanges";

// Simplified project-tracking dashboard (Feb 2026).
// Reads /api/projects directly and derives all KPIs client-side. No
// inventory / finance / procurement data is shown on the home screen any
// more — project status is the only thing that matters.

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || []))
      .catch(() => setProjects([]));
  }, []);

  if (!projects) return <div className="p-8 text-zinc-500">Loading…</div>;

  // KPIs derived from project list
  const total = projects.length;
  const active = projects.filter((p) => (p.overall_status || "active") === "active").length;
  const completed = projects.filter((p) => (p.overall_status || "") === "completed").length;
  const pendingApproval = projects.filter((p) => {
    return (p.design_status || "") === "sent" || (p.design_status || "") === "pending";
  }).length;
  const inProduction = projects.filter((p) => ["in_production", "ready", "partial"].includes(p.production_status || "")).length;
  const ready = projects.filter((p) => ["ready", "signoff_done", "completed"].includes(p.installation_status || "")).length;
  const delayed = projects.filter((p) => {
    if (!p.deadline) return false;
    try {
      return new Date(p.deadline) < new Date() && (p.overall_status !== "completed");
    } catch { return false; }
  });

  const recent = projects.slice(0, 6);

  return (
    <div className="p-6 lg:p-10" data-testid="dashboard-page">
      <PageHeader kicker="MIPL Global / Overview" title="Project command centre" />

      {/* Phase 32: Personal action queue — first thing every user sees */}
      <div className="mb-10">
        <ActionQueueWidget />
      </div>

      {["admin", "management", "scope_user"].includes(user?.role) && (
        <ScopeChangeRequests title="Scope change requests — awaiting your approval" />
      )}

      <h2 className="font-display font-bold tracking-tight text-lg mb-3 flex items-center gap-2">
        <Briefcase className="w-5 h-5" /> Projects at a glance
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat label="Total Projects" value={total} />
        <Stat label="Active" value={active} accent="text-blue-600" />
        <Stat label="Awaiting Client Approval" value={pendingApproval} accent="text-amber-600" />
        <Stat label="In Production" value={inProduction} accent="text-orange-600" />
        <Stat label="Completed" value={completed} accent="text-emerald-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <PipelineCard label="Pending Approval" icon={Clock} v={pendingApproval} tone="amber" />
        <PipelineCard label="In Production" icon={Hammer} v={inProduction} tone="orange" />
        <PipelineCard label="Ready / Delivered" icon={Truck} v={ready} tone="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-display font-bold tracking-tight">Recent Projects</h3>
            <Link to="/projects" className="text-xs font-mono text-orange-600 uppercase tracking-wider hover:underline" data-testid="link-all-projects">
              View all →
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left p-3">Reference</th><th className="text-left p-3">Name</th><th className="text-left p-3">Client</th><th className="text-right p-3">Progress</th></tr>
            </thead>
            <tbody>
              {recent.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-zinc-400">No projects yet</td></tr>}
              {recent.map((p) => (
                <tr key={p.project_code} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="p-3 font-mono text-xs">
                    <Link to={`/projects/${p.project_code}`} className="text-orange-700 hover:underline">{p.utr_no || p.project_code}</Link>
                  </td>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 text-xs text-zinc-600">{p.client_name}</td>
                  <td className="p-3 text-right font-mono text-xs">{p.progress_pct || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-flat">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <h3 className="font-display font-bold tracking-tight">Delayed projects</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left p-3">Reference</th><th className="text-left p-3">Name</th><th className="text-left p-3">Deadline</th></tr>
            </thead>
            <tbody>
              {delayed.length === 0 && <tr><td colSpan="3" className="p-8 text-center text-zinc-400">Everything on track.</td></tr>}
              {delayed.slice(0, 6).map((p) => (
                <tr key={p.project_code} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="p-3 font-mono text-xs">
                    <Link to={`/projects/${p.project_code}`} className="text-orange-700 hover:underline">{p.utr_no || p.project_code}</Link>
                  </td>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 font-mono text-xs text-rose-600">{p.deadline}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PipelineCard({ label, icon: Icon, v, tone }) {
  const tones = {
    amber: "from-amber-50 to-white text-amber-900 border-amber-200",
    orange: "from-orange-50 to-white text-orange-900 border-orange-200",
    emerald: "from-emerald-50 to-white text-emerald-900 border-emerald-200",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${tones[tone] || tones.amber} border p-5 flex items-center gap-4`}>
      <div className="w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-xs font-mono uppercase tracking-widest opacity-70">{label}</div>
        <div className="font-display text-3xl font-bold leading-tight">{v}</div>
      </div>
    </div>
  );
}
